import React, { useState, useEffect } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonIcon,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonItem,
  IonLabel,
  IonList,
  IonBadge,
  IonSpinner,
  IonNote,
  IonGrid,
  IonRow,
  IonCol,
  IonChip,
  IonText,
  IonToast,
} from '@ionic/react';
import {
  closeOutline,
  ticketOutline,
  cashOutline,
  personOutline,
  locationOutline,
  calendarOutline,
  linkOutline,
  checkmarkCircleOutline,
  alertCircleOutline,
  cubeOutline,
  unlinkOutline,
  trashOutline,
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fulfillmentService } from '../services/api';
import { Sale, Ticket } from '../types';
import { getCurrencySymbol, DEFAULT_CURRENCY } from '../utils/currencyConstants';
import { formatSplitDisplay } from '../utils/formatUtils';
import { eventBus, EVENTS } from '../utils/eventBus';

interface ConnectionDetailsModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  type: 'sale' | 'inventory';
  itemId: string | null;
}

interface SaleFulfillmentDetails {
  sale: Sale;
  fulfillments: Array<{
    _id: string;
    saleId: string;
    ticketId: Ticket;
    quantity: number;
    type: string;
    notes?: string;
    createdAt: Date;
  }>;
  totalFulfilled: number;
  totalNeeded: number;
  remainingToFulfill: number;
  isFullyFulfilled: boolean;
}

interface TicketFulfillmentDetails {
  ticket: Ticket;
  fulfillments: Array<{
    _id: string;
    saleId: Sale;
    ticketId: string;
    quantity: number;
    type: string;
    notes?: string;
    createdAt: Date;
  }>;
  totalAllocated: number;
  totalQuantity: number;
  availableQuantity: number;
  soldQuantity: number;
}

const ConnectionDetailsModal: React.FC<ConnectionDetailsModalProps> = ({
  isOpen,
  onDismiss,
  type,
  itemId,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saleDetails, setSaleDetails] = useState<SaleFulfillmentDetails | null>(null);
  const [inventoryDetails, setInventoryDetails] = useState<TicketFulfillmentDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unlinkingId, setUnlinkingId] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    console.log('ConnectionDetailsModal effect triggered:', { isOpen, itemId, type });
    if (isOpen && itemId) {
      loadConnectionDetails();
    } else if (!isOpen) {
      // Clear state when modal closes
      setSaleDetails(null);
      setInventoryDetails(null);
      setError(null);
      setUnlinkingId(null);
    }
  }, [isOpen, itemId, type]);

  const loadConnectionDetails = async () => {
    if (!itemId) return;

    console.log('ConnectionDetailsModal: Loading details for:', { type, itemId });

    setLoading(true);
    setError(null);

    try {
      if (type === 'sale') {
        const data = await fulfillmentService.getSaleFulfillment(itemId);
        console.log('Sale fulfillment data received:', data);
        console.log('Sale object:', data.sale);
        console.log('Sale ID from data:', data.sale?._id, data.sale?.id);
        setSaleDetails(data);
        setInventoryDetails(null);
      } else {
        const data = await fulfillmentService.getTicketFulfillment(itemId);
        console.log('Ticket fulfillment data received:', data);
        console.log('Ticket object:', data.ticket);
        console.log('Ticket ID from data:', data.ticket?._id, data.ticket?.id);
        setInventoryDetails(data);
        setSaleDetails(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load connection details');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlinkFromSale = async (ticketId: string) => {
    if (!saleDetails) return;
    
    // Debug logging
    console.log('Unlinking from sale - saleDetails:', saleDetails);
    console.log('Sale object:', saleDetails.sale);
    console.log('Sale ID options:', {
      _id: saleDetails.sale._id,
      id: saleDetails.sale.id,
      selected: saleDetails.sale._id || saleDetails.sale.id || ''
    });
    console.log('Ticket ID:', ticketId);
    
    try {
      setUnlinkingId(ticketId);
      await fulfillmentService.unlinkSaleFromInventory(saleDetails.sale._id || saleDetails.sale.id || '', ticketId);
      
      // Refresh the data
      await loadConnectionDetails();
      
      // Notify both sales and inventory to refresh
      eventBus.emit(EVENTS.REFRESH_TICKETS);
      eventBus.emit(EVENTS.REFRESH_SALES);
    } catch (err: any) {
      console.error('Failed to unlink:', err);
      setError(err.message || 'Failed to unlink inventory');
    } finally {
      setUnlinkingId(null);
    }
  };

  const handleUnlinkFromInventory = async (saleId: string) => {
    if (!inventoryDetails) return;
    
    // Debug logging
    console.log('Unlinking from inventory - inventoryDetails:', inventoryDetails);
    console.log('Ticket object:', inventoryDetails.ticket);
    console.log('Ticket ID options:', {
      _id: inventoryDetails.ticket._id,
      id: inventoryDetails.ticket.id,
      selected: inventoryDetails.ticket._id || inventoryDetails.ticket.id || ''
    });
    console.log('Sale ID being passed:', saleId);
    
    try {
      setUnlinkingId(saleId);
      await fulfillmentService.unlinkSaleFromInventory(saleId, inventoryDetails.ticket._id || inventoryDetails.ticket.id || '');
      
      // Refresh the data
      await loadConnectionDetails();
      
      // Notify both sales and inventory to refresh
      eventBus.emit(EVENTS.REFRESH_TICKETS);
      eventBus.emit(EVENTS.REFRESH_SALES);
    } catch (err: any) {
      console.error('Failed to unlink:', err);
      setError(err.message || 'Failed to unlink sale');
    } finally {
      setUnlinkingId(null);
    }
  };

  const renderSaleDetails = () => {
    if (!saleDetails) return null;

    const { sale, fulfillments, totalFulfilled, totalNeeded, remainingToFulfill, isFullyFulfilled } = saleDetails;

    return (
      <IonContent>

        {/* Sale Header */}
        <IonCard>
          <IonCardHeader>
            <IonCardTitle className="flex items-center">
              <IonIcon icon={cashOutline} className="mr-2" />
              Sale Details
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonGrid>
              <IonRow>
                <IonCol size="6">
                  <IonItem lines="none">
                    <IonIcon icon={personOutline} slot="start" />
                    <IonLabel>
                      <h3>Buyer</h3>
                      <p>{sale.buyerName || 'N/A'}</p>
                    </IonLabel>
                  </IonItem>
                </IonCol>
                <IonCol size="6">
                  <IonItem lines="none">
                    <IonIcon icon={calendarOutline} slot="start" />
                    <IonLabel>
                      <h3>Sale Date</h3>
                      <p>{format(new Date(sale.saleDate), 'MMM dd, yyyy')}</p>
                    </IonLabel>
                  </IonItem>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="6">
                  <IonItem lines="none">
                    <IonIcon icon={ticketOutline} slot="start" />
                    <IonLabel>
                      <h3>Quantity</h3>
                      <p>{sale.quantity || 1} tickets</p>
                    </IonLabel>
                  </IonItem>
                </IonCol>
                <IonCol size="6">
                  <IonItem lines="none">
                    <IonIcon icon={cashOutline} slot="start" />
                    <IonLabel>
                      <h3>Sale Price</h3>
                      <p>{getCurrencySymbol(sale.currency || DEFAULT_CURRENCY)}{sale.salePrice}</p>
                    </IonLabel>
                  </IonItem>
                </IonCol>
              </IonRow>
              {sale.category && (
                <IonRow>
                  <IonCol size="12">
                    <IonItem lines="none">
                      <IonIcon icon={locationOutline} slot="start" />
                      <IonLabel>
                        <h3>Category/Section</h3>
                        <p>{sale.category}{sale.block ? ` - Block ${sale.block}` : ''}{sale.row ? `, Row ${sale.row}` : ''}</p>
                      </IonLabel>
                    </IonItem>
                  </IonCol>
                </IonRow>
              )}
              {sale.split && (
                <IonRow>
                  <IonCol size="12">
                    <IonItem lines="none">
                      <IonLabel>
                        <h3>Split Requirements</h3>
                        <p>{formatSplitDisplay(sale.split)}</p>
                      </IonLabel>
                    </IonItem>
                  </IonCol>
                </IonRow>
              )}
            </IonGrid>
          </IonCardContent>
        </IonCard>

        {/* Fulfillment Status */}
        <IonCard>
          <IonCardHeader>
            <IonCardTitle className="flex items-center justify-between">
              <span className="flex items-center">
                <IonIcon icon={linkOutline} className="mr-2" />
                Fulfillment Status
              </span>
              <IonChip color={isFullyFulfilled ? 'success' : 'warning'}>
                <IonIcon icon={isFullyFulfilled ? checkmarkCircleOutline : alertCircleOutline} />
                <IonLabel>{isFullyFulfilled ? 'Fully Fulfilled' : 'Partial'}</IonLabel>
              </IonChip>
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonGrid>
              <IonRow>
                <IonCol size="4">
                  <IonText color="primary">
                    <h3>{totalFulfilled}</h3>
                    <p>Fulfilled</p>
                  </IonText>
                </IonCol>
                <IonCol size="4">
                  <IonText color="medium">
                    <h3>{totalNeeded}</h3>
                    <p>Total Needed</p>
                  </IonText>
                </IonCol>
                <IonCol size="4">
                  <IonText color={remainingToFulfill > 0 ? 'warning' : 'success'}>
                    <h3>{remainingToFulfill}</h3>
                    <p>Remaining</p>
                  </IonText>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonCardContent>
        </IonCard>

        {/* Connected Inventory */}
        <IonCard>
          <IonCardHeader>
              <IonCardTitle>Connected Inventory {!saleDetails && '(Loading...)'}</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            {(saleDetails?.fulfillments || []).length === 0 ? (
              <IonText color="medium">
                <p>No inventory connected to this sale yet.</p>
              </IonText>
            ) : (
              <IonList>
                {(saleDetails?.fulfillments || []).map((fulfillment) => (
                  <IonItem key={fulfillment._id}>
                    <IonIcon icon={cubeOutline} slot="start" />
                    <IonLabel>
                      <h3>
                        {fulfillment.ticketId.category || 'Category N/A'}
                        {fulfillment.ticketId.block ? ` - Block ${fulfillment.ticketId.block}` : ''}
                      </h3>
                      <p>
                        {fulfillment.ticketId.row ? `Row ${fulfillment.ticketId.row}` : ''}
                        {fulfillment.ticketId.seat ? `, Seat ${fulfillment.ticketId.seat}` : ''}
                        {fulfillment.ticketId.split ? ` • ${formatSplitDisplay(fulfillment.ticketId.split)}` : ''}
                      </p>
                      <IonNote>
                        Purchase Price: {getCurrencySymbol(fulfillment.ticketId.currency || DEFAULT_CURRENCY)}{fulfillment.ticketId.purchasePrice}
                        {fulfillment.ticketId.sourceType && (
                          <> • Source: {fulfillment.ticketId.sourceType === 'supplier' ? `Supplier: ${fulfillment.ticketId.supplier || 'N/A'}` : fulfillment.ticketId.sourceType === 'site' ? (fulfillment.ticketId.supplier || 'Site') : fulfillment.ticketId.sourceType}</>
                        )}
                      </IonNote>
                    </IonLabel>
                    <div className="flex items-center gap-2">
                      <IonBadge color="primary">
                        {fulfillment.quantity} tickets
                      </IonBadge>
                      <IonButton
                        fill="clear"
                        size="small"
                        color="danger"
                        onClick={() => handleUnlinkFromSale(fulfillment.ticketId._id || fulfillment.ticketId.id || '')}
                        disabled={unlinkingId === (fulfillment.ticketId._id || fulfillment.ticketId.id)}
                      >
                        {unlinkingId === (fulfillment.ticketId._id || fulfillment.ticketId.id) ? (
                          <IonSpinner name="dots" className="w-4 h-4" />
                        ) : (
                          <IonIcon icon={unlinkOutline} />
                        )}
                      </IonButton>
                    </div>
                  </IonItem>
                ))}
              </IonList>
            )}
          </IonCardContent>
        </IonCard>
      </IonContent>
    );
  };

  const renderInventoryDetails = () => {
    if (!inventoryDetails) return null;

    const { ticket, fulfillments, totalAllocated, totalQuantity, availableQuantity } = inventoryDetails;

    return (
      <IonContent>
        {/* Inventory Header */}
        <IonCard>
          <IonCardHeader>
            <IonCardTitle className="flex items-center">
              <IonIcon icon={cubeOutline} className="mr-2" />
              Inventory Details
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonGrid>
              <IonRow>
                <IonCol size="6">
                  <IonItem lines="none">
                    <IonIcon icon={locationOutline} slot="start" />
                    <IonLabel>
                      <h3>Category/Section</h3>
                      <p>{ticket.category || 'N/A'}{ticket.block ? ` - Block ${ticket.block}` : ''}</p>
                    </IonLabel>
                  </IonItem>
                </IonCol>
                <IonCol size="6">
                  <IonItem lines="none">
                    <IonIcon icon={calendarOutline} slot="start" />
                    <IonLabel>
                      <h3>Event Date</h3>
                      <p>{format(new Date(ticket.eventDate), 'MMM dd, yyyy')}</p>
                    </IonLabel>
                  </IonItem>
                </IonCol>
              </IonRow>
              {(ticket.row || ticket.seat) && (
                <IonRow>
                  <IonCol size="12">
                    <IonItem lines="none">
                      <IonLabel>
                        <h3>Location</h3>
                        <p>
                          {ticket.row ? `Row ${ticket.row}` : ''}
                          {ticket.seat ? `, Seat ${ticket.seat}` : ''}
                        </p>
                      </IonLabel>
                    </IonItem>
                  </IonCol>
                </IonRow>
              )}
              <IonRow>
                <IonCol size="6">
                  <IonItem lines="none">
                    <IonIcon icon={cashOutline} slot="start" />
                    <IonLabel>
                      <h3>Purchase Price</h3>
                      <p>{getCurrencySymbol(ticket.currency || DEFAULT_CURRENCY)}{ticket.purchasePrice}</p>
                    </IonLabel>
                  </IonItem>
                </IonCol>
                <IonCol size="6">
                  <IonItem lines="none">
                    <IonLabel>
                      <h3>Split Type</h3>
                      <p>{formatSplitDisplay(ticket.split || 'together')}</p>
                    </IonLabel>
                  </IonItem>
                </IonCol>
              </IonRow>
              {ticket.sourceType && (
                <IonRow>
                  <IonCol size="12">
                    <IonItem lines="none">
                      <IonLabel>
                        <h3>Source</h3>
                        <p>{ticket.sourceType === 'supplier' ? `Supplier: ${ticket.supplier || 'N/A'}` : ticket.sourceType}</p>
                      </IonLabel>
                    </IonItem>
                  </IonCol>
                </IonRow>
              )}
            </IonGrid>
          </IonCardContent>
        </IonCard>

        {/* Allocation Status */}
        <IonCard>
          <IonCardHeader>
            <IonCardTitle className="flex items-center">
              <IonIcon icon={linkOutline} className="mr-2" />
              Allocation Status
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonGrid>
              <IonRow>
                <IonCol size="3">
                  <IonText color="primary">
                    <h3>{totalQuantity}</h3>
                    <p>Total</p>
                  </IonText>
                </IonCol>
                <IonCol size="3">
                  <IonText color="warning">
                    <h3>{totalAllocated}</h3>
                    <p>Allocated</p>
                  </IonText>
                </IonCol>
                <IonCol size="3">
                  <IonText color="success">
                    <h3>{availableQuantity}</h3>
                    <p>Available</p>
                  </IonText>
                </IonCol>
                <IonCol size="3">
                  <IonText color="medium">
                    <h3>{inventoryDetails.soldQuantity}</h3>
                    <p>Sold</p>
                  </IonText>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonCardContent>
        </IonCard>

        {/* Connected Sales */}
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Connected Sales</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            {fulfillments.length === 0 ? (
              <IonText color="medium">
                <p>No sales connected to this inventory yet.</p>
              </IonText>
            ) : (
              <IonList>
                {fulfillments.map((fulfillment) => (
                  <IonItem key={fulfillment._id}>
                    <IonIcon icon={cashOutline} slot="start" />
                    <IonLabel>
                      <h3>
                        {fulfillment.saleId.buyerName || 'Buyer N/A'}
                        {fulfillment.saleId.orderNumber ? ` (#${fulfillment.saleId.orderNumber})` : ''}
                      </h3>
                      <p>
                        Sale Date: {format(new Date(fulfillment.saleId.saleDate), 'MMM dd, yyyy')}
                        {fulfillment.saleId.category ? ` • ${fulfillment.saleId.category}` : ''}
                      </p>
                      <IonNote>
                        Sale Price: {getCurrencySymbol(fulfillment.saleId.currency || DEFAULT_CURRENCY)}{fulfillment.saleId.salePrice}
                        {fulfillment.saleId.split ? ` • ${formatSplitDisplay(fulfillment.saleId.split)}` : ''}
                      </IonNote>
                    </IonLabel>
                    <div className="flex items-center gap-2">
                      <IonBadge color="success">
                        {fulfillment.quantity} tickets
                      </IonBadge>
                      <IonButton
                        fill="clear"
                        size="small"
                        color="danger"
                        onClick={() => handleUnlinkFromInventory(fulfillment.saleId._id || fulfillment.saleId.id || '')}
                        disabled={unlinkingId === (fulfillment.saleId._id || fulfillment.saleId.id)}
                      >
                        {unlinkingId === (fulfillment.saleId._id || fulfillment.saleId.id) ? (
                          <IonSpinner name="dots" className="w-4 h-4" />
                        ) : (
                          <IonIcon icon={unlinkOutline} />
                        )}
                      </IonButton>
                    </div>
                  </IonItem>
                ))}
              </IonList>
            )}
          </IonCardContent>
        </IonCard>
      </IonContent>
    );
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDismiss}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>
            {type === 'sale' ? 'Sale Connection Details' : 'Inventory Connection Details'}
          </IonTitle>
          <IonButtons slot="end">
            <IonButton fill="clear" onClick={onDismiss}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      {loading ? (
        <IonContent>
          <div className="flex items-center justify-center py-12">
            <IonSpinner name="crescent" className="w-8 h-8" color="primary" />
          </div>
        </IonContent>
      ) : error ? (
        <IonContent>
          <div className="px-4 py-8">
            <IonText color="danger">
              <h3>Error Loading Details</h3>
              <p>{error}</p>
            </IonText>
          </div>
        </IonContent>
      ) : type === 'sale' ? (
        renderSaleDetails()
      ) : (
        renderInventoryDetails()
      )}
      
      <IonToast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        duration={3000}
        position="bottom"
      />
    </IonModal>
  );
};

export default ConnectionDetailsModal;